import {
  FilePlus,
  Pencil,
  Trash,
  FileText,
  Clock,
  Eye,
  History,
} from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useUser } from '~/context/UserContext'
import {
  useGetDocuments,
  useCreateDocument,
  useUpdateDocument,
  useDeleteDocument,
  useGetDocumentVersions,
  useGetDocumentVersion,
  Document,
  DocumentVersion,
} from '~/stores/documentLibraryStore'

const DocumentManager: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true)
  const [dialogVisible, setDialogVisible] = useState<boolean>(false)
  const [viewDocumentDialogVisible, setViewDocumentDialogVisible] =
    useState<boolean>(false)
  const [viewVersionsDialogVisible, setViewVersionsDialogVisible] =
    useState<boolean>(false)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  )
  const [documentVersions, setDocumentVersions] = useState<DocumentVersion[]>(
    []
  )
  const [selectedVersion, setSelectedVersion] =
    useState<DocumentVersion | null>(null)
  const [isNewDocument, setIsNewDocument] = useState<boolean>(true)
  const [formDocument, setFormDocument] = useState<Partial<Document>>({
    documentKey: '',
    title: '',
    category: '',
    purpose: '',
    extraContext: '',
    userRoles: [],
    producers: [],
    currentMarkdown: '',
    tags: [],
    isKnowledgeSet: false,
    isPublic: false,
  })

  const [categories, setCategories] = useState<string[]>([])
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const user = useUser()

  const [importDialogVisible, setImportDialogVisible] = useState<boolean>(false)
  const [importJsonParams, setImportJsonParams] = useState<string>('')
  const [importMarkdown, setImportMarkdown] = useState<string>('')
  const [importJsonError, setImportJsonError] = useState<string>('')

  // State for the delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(
    null
  )

  // Get documents from store
  const {
    documentsLoading,
    documentsFetchSuccess,
    documentsFetchError,
    documents,
    documentsErrorMessage,
    fetchDocuments,
  } = useGetDocuments((state) => ({
    documentsLoading: state.documentsLoading,
    documentsFetchSuccess: state.documentsFetchSuccess,
    documentsFetchError: state.documentsFetchError,
    documents: state.documents,
    documentsErrorMessage: state.documentsErrorMessage,
    fetchDocuments: state.fetchDocuments,
  }))

  // Get hooks for CRUD operations
  const createDocument = useCreateDocument()
  const updateDocument = useUpdateDocument()
  const deleteDocumentHook = useDeleteDocument()
  const getDocumentVersions = useGetDocumentVersions()
  const getDocumentVersion = useGetDocumentVersion()

  // Fetch documents on component mount
  useEffect(() => {
    setLoading(true)
    fetchDocuments()
  }, [fetchDocuments])

  // Update local state when documents are loaded
  useEffect(() => {
    if (documentsFetchSuccess && documents) {
      // Extract unique categories and tags
      const uniqueCategories = [
        ...new Set(documents.map((doc) => doc.category)),
      ]
      setCategories(uniqueCategories)

      const allTags = documents.flatMap((doc) => doc.tags || [])
      const uniqueTags = [...new Set(allTags)]
      setAvailableTags(uniqueTags)

      setLoading(false)
    }
  }, [documents, documentsFetchSuccess])

  // Display error message if document fetch fails
  useEffect(() => {
    if (documentsFetchError) {
      toast.error(documentsErrorMessage || 'Failed to load documents')
      setLoading(false)
    }
  }, [documentsFetchError, documentsErrorMessage])

  const fetchDocumentVersions = (documentId: string) => {
    getDocumentVersions.execute({
      id: documentId,
      onSuccess: (response) => {
        setDocumentVersions(response.data.data)
      },
      onError: (error) => {
        console.error('Error fetching document versions:', error)
        toast.error('Failed to load document versions')
      },
    })
  }

  const openNewDocumentDialog = () => {
    setFormDocument({
      documentKey: '',
      title: '',
      category: '',
      purpose: '',
      extraContext: '',
      userRoles: [],
      producers: [],
      currentMarkdown: '',
      tags: [],
      isKnowledgeSet: false,
      isPublic: false,
    })
    setIsNewDocument(true)
    setDialogVisible(true)
  }

  const openEditDocumentDialog = (document: Document) => {
    setFormDocument({ ...document })
    setIsNewDocument(false)
    setDialogVisible(true)
  }

  const openViewDocumentDialog = (document: Document) => {
    console.log('Selected document:', document)
    setSelectedDocument(document)
    setViewDocumentDialogVisible(true)
  }

  const openViewVersionsDialog = (document: Document) => {
    setSelectedDocument(document)
    fetchDocumentVersions(document.id)
    setViewVersionsDialogVisible(true)
  }

  const handleFormChange = (field: string, value: any) => {
    setFormDocument((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const saveDocument = () => {
    if (isNewDocument) {
      createDocument.execute({
        data: formDocument,
        onSuccess: (response) => {
          toast.success('Document created successfully')
          setDialogVisible(false)
          fetchDocuments() // Refresh the list
        },
        onError: (error) => {
          console.error('Error creating document:', error)
          toast.error(
            error.response?.data?.error || 'Failed to create document'
          )
        },
      })
    } else {
      updateDocument.execute({
        id: formDocument.id,
        data: formDocument,
        onSuccess: (response) => {
          toast.success('Document updated successfully')
          setDialogVisible(false)
          fetchDocuments() // Refresh the list
        },
        onError: (error) => {
          console.error('Error updating document:', error)
          toast.error(
            error.response?.data?.error || 'Failed to update document'
          )
        },
      })
    }
  }

  // Replace confirmDialog with state management for AlertDialog
  const confirmDelete = (document: Document) => {
    setDocumentToDelete(document)
    setDeleteDialogOpen(true)
  }

  const handleDeleteDocument = (documentId: string) => {
    deleteDocumentHook.execute({
      id: documentId,
      onSuccess: () => {
        toast.success('Document deleted successfully')
        fetchDocuments() // Refresh the list
      },
      onError: (error) => {
        console.error('Error deleting document:', error)
        toast.error(error.response?.data?.error || 'Failed to delete document')
      },
    })
  }

  const handleDeleteConfirmed = () => {
    if (documentToDelete) {
      handleDeleteDocument(documentToDelete.id)
    }
    setDeleteDialogOpen(false)
    setDocumentToDelete(null)
  }

  const viewVersion = (version: DocumentVersion) => {
    setSelectedVersion(version)
  }

  const openImportDialog = () => {
    setImportJsonParams('')
    setImportMarkdown('')
    setImportJsonError('')
    setImportDialogVisible(true)
  }

  const validateAndParseJson = (
    jsonString: string
  ): Partial<Document> | null => {
    try {
      const parsed = JSON.parse(jsonString)
      // Validate required fields
      if (
        !parsed.documentKey ||
        !parsed.title ||
        !parsed.category ||
        !parsed.purpose
      ) {
        setImportJsonError(
          'JSON must include documentKey, title, category, and purpose'
        )
        return null
      }
      return parsed
    } catch (e) {
      setImportJsonError('Invalid JSON format')
      return null
    }
  }

  const handleImportSubmit = () => {
    setImportJsonError('')
    const parsedJson = validateAndParseJson(importJsonParams)
    if (!parsedJson) return

    if (!importMarkdown.trim()) {
      setImportJsonError('Markdown template is required')
      return
    }

    // Combine JSON params with markdown
    const documentData: Partial<Document> = {
      ...parsedJson,
      currentMarkdown: importMarkdown,
      userRoles: parsedJson.userRoles || [],
      producers: parsedJson.producers || [],
      tags: parsedJson.tags || [],
    }

    createDocument.execute({
      data: documentData,
      onSuccess: (response) => {
        toast.success('Document imported successfully')
        setImportDialogVisible(false)
        fetchDocuments()
      },
      onError: (error) => {
        console.error('Error importing document:', error)
        toast.error(error.response?.data?.error || 'Failed to import document')
      },
    })
  }

  // Create pagination state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [sortField, setSortField] = useState('updatedAt')
  const [sortOrder, setSortOrder] = useState(-1) // descending

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage)
    setPage(0) // Reset to first page when changing rows per page
  }

  // Function to handle sorting
  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === 1
    setSortOrder(isAsc ? -1 : 1)
    setSortField(field)
  }

  // Sort and paginate documents
  const sortedDocuments = React.useMemo(() => {
    const sorted = [...(documents || [])].sort((a, b) => {
      if (a[sortField] < b[sortField]) return -1 * sortOrder
      if (a[sortField] > b[sortField]) return 1 * sortOrder
      return 0
    })

    return sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  }, [documents, page, rowsPerPage, sortField, sortOrder])

  // Template for action buttons in the table
  const actionBodyTemplate = (rowData: Document) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-rounded p-button-info p-button-text"
          onClick={() => openViewDocumentDialog(rowData)}
          tooltip="View"
        />
        <Button
          icon="pi pi-history"
          className="p-button-rounded p-button-help p-button-text"
          onClick={() => openViewVersionsDialog(rowData)}
          tooltip="View Versions"
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success p-button-text"
          onClick={() => openEditDocumentDialog(rowData)}
          tooltip="Edit"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-text"
          onClick={() => confirmDelete(rowData)}
          tooltip="Delete"
        />
      </div>
    )
  }

  // Template for displaying arrays (tags, userRoles, producers)
  const arrayBodyTemplate = (rowData: Document, field: keyof Document) => {
    const array = rowData[field] as string[]
    if (!array || array.length === 0) return '-'
    return array.join(', ')
  }

  // Format date for display
  const dateBodyTemplate = (rowData: Document, field: keyof Document) => {
    const date = new Date(rowData[field] as string)
    return date.toLocaleString()
  }

  // Document form dialog
  const renderDocumentDialog = () => {
    const dialogHeader = isNewDocument ? 'Create New Document' : 'Edit Document'

    return (
      <Dialog open={dialogVisible} onOpenChange={setDialogVisible}>
        <DialogContent className="sm:max-w-[90%] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogHeader}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <Label htmlFor="documentKey" className="font-bold">
                  Document Key*
                </Label>
                <Input
                  id="documentKey"
                  value={formDocument.documentKey || ''}
                  onChange={(e) =>
                    handleFormChange('documentKey', e.target.value)
                  }
                  disabled={!isNewDocument}
                  className="w-full"
                  required
                />
                <p className="text-sm text-muted-foreground">
                  Unique identifier (e.g., "SAM-PROD")
                </p>
              </div>

              <div>
                <Label htmlFor="title" className="font-bold">
                  Title*
                </Label>
                <Input
                  id="title"
                  value={formDocument.title || ''}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  className="w-full"
                  required
                />
              </div>

              <div>
                <Label htmlFor="category" className="font-bold">
                  Category*
                </Label>
                <Select
                  value={formDocument.category || ''}
                  onValueChange={(value) => handleFormChange('category', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select or enter a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="purpose" className="font-bold">
                  Purpose*
                </Label>
                <Textarea
                  id="purpose"
                  value={formDocument.purpose || ''}
                  onChange={(e) => handleFormChange('purpose', e.target.value)}
                  className="w-full"
                  rows={3}
                  required
                />
              </div>

              <div>
                <Label htmlFor="extraContext" className="font-bold">
                  Extra Context
                </Label>
                <Textarea
                  id="extraContext"
                  value={formDocument.extraContext || ''}
                  onChange={(e) =>
                    handleFormChange('extraContext', e.target.value)
                  }
                  className="w-full"
                  rows={3}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="userRoles" className="font-bold">
                  User Roles
                </Label>
                <Textarea
                  id="userRoles"
                  value={
                    formDocument.userRoles
                      ? formDocument.userRoles.join(', ')
                      : ''
                  }
                  onChange={(e) =>
                    handleFormChange(
                      'userRoles',
                      e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter((s) => s)
                    )
                  }
                  className="w-full"
                  rows={2}
                  placeholder="Enter roles separated by commas"
                />
                <p className="text-sm text-muted-foreground">
                  Who needs this kind of material (comma-separated)
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="producers" className="font-bold">
                  Producers
                </Label>
                <Textarea
                  id="producers"
                  value={
                    formDocument.producers
                      ? formDocument.producers.join(', ')
                      : ''
                  }
                  onChange={(e) =>
                    handleFormChange(
                      'producers',
                      e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter((s) => s)
                    )
                  }
                  className="w-full"
                  rows={2}
                  placeholder="Enter producers separated by commas"
                />
                <p className="text-sm text-muted-foreground">
                  Who produces this kind of material (comma-separated)
                </p>
              </div>
            </div>

            <div className="space-y-4 col-span-2">
              <div>
                <Label htmlFor="tags" className="font-bold">
                  Tags
                </Label>
                <Textarea
                  id="tags"
                  value={formDocument.tags ? formDocument.tags.join(', ') : ''}
                  onChange={(e) =>
                    handleFormChange(
                      'tags',
                      e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter((s) => s)
                    )
                  }
                  className="w-full"
                  rows={2}
                  placeholder="Enter tags separated by commas"
                />
                <p className="text-sm text-muted-foreground">
                  Keywords to help categorize and find documents
                  (comma-separated)
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isKnowledgeSet"
                  checked={formDocument.isKnowledgeSet || false}
                  onCheckedChange={(checked) =>
                    handleFormChange('isKnowledgeSet', checked === true)
                  }
                />
                <Label htmlFor="isKnowledgeSet" className="font-bold">
                  Include in Knowledge Set
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Choose this option to make this document available for AI
                knowledge retrieval
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isPublic"
                  checked={formDocument.isPublic || false}
                  onCheckedChange={(checked) =>
                    handleFormChange('isPublic', checked === true)
                  }
                />
                <Label htmlFor="isPublic" className="font-bold">
                  Public
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Choose this option to allow public access to this document
              </p>
            </div>

            <div className="col-span-2 space-y-4">
              <div>
                <Label htmlFor="markdown" className="font-bold">
                  Content (Markdown)
                </Label>
                <Textarea
                  id="markdown"
                  value={formDocument.currentMarkdown || ''}
                  onChange={(e) =>
                    handleFormChange('currentMarkdown', e.target.value)
                  }
                  className="w-full font-mono"
                  rows={12}
                />
                <p className="text-sm text-muted-foreground">
                  {!isNewDocument && 'Saving will create a new version. '}
                  Enter change notes below.
                </p>
              </div>
            </div>

            {!isNewDocument && (
              <div className="col-span-2">
                <div>
                  <Label htmlFor="changeNotes" className="font-bold">
                    Change Notes
                  </Label>
                  <Textarea
                    id="changeNotes"
                    value={formDocument.changeNotes || ''}
                    onChange={(e) =>
                      handleFormChange('changeNotes', e.target.value)
                    }
                    className="w-full"
                    rows={2}
                    placeholder="Describe what changed in this version"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogVisible(false)}>
              Cancel
            </Button>
            <Button onClick={saveDocument}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // View document dialog
  const renderViewDocumentDialog = () => {
    if (!selectedDocument) return null

    return (
      <Dialog
        open={viewDocumentDialogVisible}
        onOpenChange={setViewDocumentDialogVisible}
      >
        <DialogContent className="sm:max-w-[90%] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Document: {selectedDocument.title}</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="details">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="raw">Raw Markdown</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold">Document Key:</h3>
                    <p>{selectedDocument.documentKey}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Category:</h3>
                    <p>{selectedDocument.category}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Current Version:</h3>
                    <p>{selectedDocument.currentVersion}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Created:</h3>
                    <p>
                      {new Date(selectedDocument.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Last Updated:</h3>
                    <p>
                      {new Date(selectedDocument.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold">Purpose:</h3>
                    <p className="whitespace-pre-wrap">
                      {selectedDocument.purpose}
                    </p>
                  </div>
                  {selectedDocument.extraContext && (
                    <div>
                      <h3 className="font-semibold">Extra Context:</h3>
                      <p className="whitespace-pre-wrap">
                        {selectedDocument.extraContext}
                      </p>
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold">User Roles:</h3>
                    <p>{selectedDocument.userRoles.join(', ') || '-'}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Producers:</h3>
                    <p>{selectedDocument.producers.join(', ') || '-'}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Tags:</h3>
                    <p>{selectedDocument.tags.join(', ') || '-'}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Knowledge Set:</h3>
                    <p>{selectedDocument.isKnowledgeSet ? 'Yes' : 'No'}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold">Public:</h3>
                    <p>{selectedDocument.isPublic ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold">Content:</h3>
                <div className="border rounded-md p-4 mt-2 max-h-[400px] overflow-auto">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: selectedDocument.currentMarkdown,
                    }}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="raw">
              <div className="border rounded-md p-3 max-h-[600px] overflow-auto">
                <pre className="whitespace-pre-wrap">
                  {selectedDocument.currentMarkdown}
                </pre>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    )
  }

  // View versions dialog
  const renderVersionsDialog = () => {
    if (!selectedDocument) return null

    return (
      <Dialog
        header={`Versions: ${selectedDocument.title}`}
        visible={viewVersionsDialogVisible}
        style={{ width: '90%', maxWidth: '1200px' }}
        onHide={() => {
          setViewVersionsDialogVisible(false)
          setSelectedVersion(null)
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-4">
            <h3>Version History</h3>
            <ScrollArea className="h-[400px] rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documentVersions.map((version, index) => (
                    <TableRow
                      key={version.versionNumber}
                      onClick={() => viewVersion(version)}
                      className={`cursor-pointer ${index % 2 === 0 ? 'bg-muted' : ''} ${
                        selectedVersion === version
                          ? 'bg-accent text-accent-foreground'
                          : ''
                      }`}
                    >
                      <TableCell>{version.versionNumber}</TableCell>
                      <TableCell>
                        {new Date(version.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>{version.changeNotes}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>

          <div className="col-12 md:col-8">
            {selectedVersion ? (
              <>
                <h3>Version {selectedVersion.versionNumber} Content</h3>
                <Tabs defaultValue="rendered" className="w-full">
                  <TabsList>
                    <TabsTrigger value="rendered">Rendered</TabsTrigger>
                    <TabsTrigger value="raw">Raw Markdown</TabsTrigger>
                  </TabsList>
                  <TabsContent value="rendered">
                    <div
                      className="border border-gray-200 rounded-md p-4"
                      style={{ maxHeight: '400px', overflow: 'auto' }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: selectedVersion.markdown,
                        }}
                      />
                    </div>
                  </TabsContent>
                  <TabsContent value="raw">
                    <div
                      className="border border-gray-200 rounded-md p-3"
                      style={{ maxHeight: '400px', overflow: 'auto' }}
                    >
                      <pre className="whitespace-pre-wrap">
                        {selectedVersion.markdown}
                      </pre>
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            ) : (
              <div className="flex align-items-center justify-content-center h-full">
                <p className="text-center">Select a version to view content</p>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    )
  }

  const renderImportDialog = () => {
    return (
      <Dialog open={importDialogVisible} onOpenChange={setImportDialogVisible}>
        <DialogContent className="sm:max-w-[90%] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Import Document Template</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 overflow-y-auto pr-2">
            <div>
              <Label className="font-bold">
                JSON Parameters*
                {importJsonError && (
                  <span className="ml-2 text-destructive text-sm">
                    {importJsonError}
                  </span>
                )}
              </Label>
              <Textarea
                value={importJsonParams}
                onChange={(e) => setImportJsonParams(e.target.value)}
                rows={8}
                className="w-full font-mono"
                placeholder={`{
  "documentKey": "TEMPLATE-001",
  "title": "Template Title",
  "category": "Category",
  "purpose": "Purpose description",
  "extraContext": "Additional context",
  "userRoles": ["role1", "role2"],
  "producers": ["producer1", "producer2"],
  "tags": ["tag1", "tag2"]
}`}
              />
              <p className="text-sm text-muted-foreground">
                Enter document metadata as JSON
              </p>
            </div>

            <div>
              <Label className="font-bold">Markdown Template*</Label>
              <Textarea
                value={importMarkdown}
                onChange={(e) => setImportMarkdown(e.target.value)}
                rows={8}
                className="w-full font-mono"
                placeholder="# Document Title

## Section 1
Content for section 1...

## Section 2
Content for section 2..."
              />
              <p className="text-sm text-muted-foreground">
                Enter the document template in markdown format
              </p>
            </div>
          </div>

          <DialogFooter className="mt-4 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => setImportDialogVisible(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleImportSubmit}>Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div>
      {/* Remove Toast ref component */}

      {/* Replace ConfirmDialog with AlertDialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Confirmation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{documentToDelete?.title}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirmed}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex justify-between items-center mb-3">
        <h2 className="m-0">Document Library</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={openImportDialog}
            className="flex items-center"
          >
            <FilePlus className="mr-2 h-4 w-4" />
            Import Template
          </Button>
          <Button onClick={openNewDocumentDialog} className="flex items-center">
            <FilePlus className="mr-2 h-4 w-4" />
            New Document
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort('documentKey')}
              >
                Key{' '}
                {sortField === 'documentKey' && (sortOrder === 1 ? '↑' : '↓')}
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort('title')}
              >
                Title {sortField === 'title' && (sortOrder === 1 ? '↑' : '↓')}
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort('category')}
              >
                Category{' '}
                {sortField === 'category' && (sortOrder === 1 ? '↑' : '↓')}
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort('currentVersion')}
              >
                Version{' '}
                {sortField === 'currentVersion' &&
                  (sortOrder === 1 ? '↑' : '↓')}
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort('updatedAt')}
              >
                Updated{' '}
                {sortField === 'updatedAt' && (sortOrder === 1 ? '↑' : '↓')}
              </TableHead>
              <TableHead>Actions</TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort('isKnowledgeSet')}
              >
                Knowledge Set{' '}
                {sortField === 'isKnowledgeSet' &&
                  (sortOrder === 1 ? '↑' : '↓')}
              </TableHead>
              <TableHead
                className="cursor-pointer"
                onClick={() => handleSort('isPublic')}
              >
                Public{' '}
                {sortField === 'isPublic' && (sortOrder === 1 ? '↑' : '↓')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading || documentsLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center p-4">
                  Loading documents...
                </TableCell>
              </TableRow>
            ) : sortedDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center p-4">
                  No documents found
                </TableCell>
              </TableRow>
            ) : (
              sortedDocuments.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>{doc.documentKey}</TableCell>
                  <TableCell>{doc.title}</TableCell>
                  <TableCell>{doc.category}</TableCell>
                  <TableCell>{doc.currentVersion}</TableCell>
                  <TableCell>
                    {new Date(doc.updatedAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openEditDocumentDialog(doc)}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openViewDocumentDialog(doc)}
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openViewVersionsDialog(doc)}
                        title="View Versions"
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => confirmDelete(doc)}
                        title="Delete"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>{doc.isKnowledgeSet ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{doc.isPublic ? 'Yes' : 'No'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4"></div>
      <div className="text-sm text-muted-foreground">
        Showing {sortedDocuments.length > 0 ? page * rowsPerPage + 1 : 0} to{' '}
        {Math.min((page + 1) * rowsPerPage, (documents || []).length)} of{' '}
        {(documents || []).length} entries
      </div>

      <div className="flex items-center space-x-2">
        <Label htmlFor="rows-per-page">Show:</Label>
        <Select
          value={rowsPerPage.toString()}
          onValueChange={(value) => handleRowsPerPageChange(Number(value))}
        >
          <SelectTrigger className="w-[70px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">5</SelectItem>
            <SelectItem value="10">10</SelectItem>
            <SelectItem value="25">25</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Pagination>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={() => handlePageChange(Math.max(0, page - 1))}
              disabled={page === 0}
              aria-disabled={page === 0}
            />
          </PaginationItem>

          {Array.from(
            { length: Math.ceil((documents || []).length / rowsPerPage) },
            (_, i) => (
              <PaginationItem key={i}>
                <PaginationLink
                  onClick={() => handlePageChange(i)}
                  isActive={page === i}
                >
                  {i + 1}
                </PaginationLink>
              </PaginationItem>
            )
          )}

          <PaginationItem>
            <PaginationNext
              onClick={() =>
                handlePageChange(
                  Math.min(
                    Math.ceil((documents || []).length / rowsPerPage) - 1,
                    page + 1
                  )
                )
              }
              disabled={
                page >= Math.ceil((documents || []).length / rowsPerPage) - 1
              }
              aria-disabled={
                page >= Math.ceil((documents || []).length / rowsPerPage) - 1
              }
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      {renderDocumentDialog()}
      {renderViewDocumentDialog()}
      {renderVersionsDialog()}
      {renderImportDialog()}
    </div>
  )
}

export default DocumentManager
